import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { payrollAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { 
  DollarSign, 
  Download, 
  Eye,
  Calendar,
  TrendingUp,
  Building2,
  FileText,
  Printer
} from 'lucide-react';
import { toast } from 'sonner';

const PayslipCard = ({ payroll, onClick }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    processed: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700'
  };

  return (
    <Card 
      className="cursor-pointer card-hover" 
      onClick={() => onClick(payroll)}
      data-testid={`payslip-card-${payroll.id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {payroll.month} {payroll.year}
            </h3>
            <p className="text-sm text-slate-500">Payslip #{payroll.id?.slice(0, 8)}</p>
          </div>
          <Badge className={statusColors[payroll.status]}>
            {payroll.status}
          </Badge>
        </div>
        <Separator className="my-4" />
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Base Salary</span>
            <span className="font-medium text-slate-900">R {payroll.base_salary?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Deductions</span>
            <span className="font-medium text-red-600">- R {((payroll.tax || 0) + (payroll.uif || 0) + (payroll.pension || 0) + (payroll.medical_aid || 0)).toLocaleString()}</span>
          </div>
        </div>
        <Separator className="my-4" />
        <div className="flex justify-between items-center">
          <span className="text-slate-600 font-medium">Net Pay</span>
          <span className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
            R {payroll.net_salary?.toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const PayslipDetail = ({ payroll, onClose }) => {
  const { user } = useAuth();

  const handlePrint = () => {
    toast.success('Preparing payslip for print...');
    window.print();
  };

  const handleDownload = () => {
    toast.success('Downloading payslip...');
  };

  return (
    <Dialog open={!!payroll} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <FileText className="w-5 h-5 text-indigo-600" />
            Payslip - {payroll?.month} {payroll?.year}
          </DialogTitle>
          <DialogDescription>
            Employee ID: {user?.employee_id}
          </DialogDescription>
        </DialogHeader>
        
        <div className="payslip mt-4">
          {/* Header */}
          <div className="payslip-header">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Qfinity AI Workforce</h2>
                <p className="text-indigo-100 text-sm">HR Intelligence Platform</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-indigo-100">Pay Date</p>
                <p className="font-semibold">{payroll?.pay_date}</p>
              </div>
            </div>
          </div>

          {/* Employee Info */}
          <div className="payslip-body">
            <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">Employee Name</p>
                <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Employee ID</p>
                <p className="font-semibold text-slate-900 font-mono">{user?.employee_id}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Department</p>
                <p className="font-semibold text-slate-900">{user?.department}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Position</p>
                <p className="font-semibold text-slate-900">{user?.position}</p>
              </div>
            </div>

            {/* Earnings */}
            <h4 className="font-semibold text-slate-900 mb-3">Earnings</h4>
            <div className="space-y-2 mb-6">
              <div className="payslip-row">
                <span className="text-slate-600">Base Salary</span>
                <span className="font-medium text-slate-900">R {payroll?.base_salary?.toLocaleString()}</span>
              </div>
              <div className="payslip-row">
                <span className="text-slate-600">Overtime</span>
                <span className="font-medium text-slate-900">R {payroll?.overtime?.toLocaleString()}</span>
              </div>
              <div className="payslip-row">
                <span className="text-slate-600">Bonus</span>
                <span className="font-medium text-slate-900">R {payroll?.bonus?.toLocaleString()}</span>
              </div>
              <div className="payslip-row bg-emerald-50 -mx-6 px-6 py-2">
                <span className="font-semibold text-emerald-700">Gross Earnings</span>
                <span className="font-bold text-emerald-700">
                  R {(payroll?.base_salary + payroll?.overtime + payroll?.bonus)?.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Deductions */}
            <h4 className="font-semibold text-slate-900 mb-3">Deductions</h4>
            <div className="space-y-2 mb-6">
              <div className="payslip-row">
                <span className="text-slate-600">Income Tax (PAYE)</span>
                <span className="font-medium text-red-600">- R {payroll?.tax?.toLocaleString()}</span>
              </div>
              <div className="payslip-row">
                <span className="text-slate-600">Pension & Medical</span>
                <span className="font-medium text-red-600">- R {((payroll?.pension || 0) + (payroll?.medical_aid || 0) + (payroll?.uif || 0)).toLocaleString()}</span>
              </div>
              <div className="payslip-row bg-red-50 -mx-6 px-6 py-2">
                <span className="font-semibold text-red-700">Total Deductions</span>
                <span className="font-bold text-red-700">
                  - R {((payroll?.tax || 0) + (payroll?.pension || 0) + (payroll?.medical_aid || 0) + (payroll?.uif || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Net Pay */}
            <div className="payslip-total">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-slate-900">Net Pay</span>
                <span className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  R {payroll?.net_salary?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={handlePrint} className="flex-1">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleDownload} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Payslips = () => {
  const { user } = useAuth();
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  useEffect(() => {
    fetchPayrolls();
  }, [user?.employee_id]);

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const response = await payrollAPI.getByEmployee(user?.employee_id || '100001');
      setPayrolls(response.data);
    } catch (error) {
      console.error('Payroll fetch error:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const totalEarnings = payrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0);
  const totalTax = payrolls.reduce((sum, p) => sum + (p.tax || 0), 0);
  const avgSalary = payrolls.length > 0 ? totalEarnings / payrolls.length : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading payroll data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payslips-page">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Earnings (YTD)</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  R {totalEarnings.toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Tax Paid</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  R {totalTax.toLocaleString()}
                </p>
              </div>
              <Building2 className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Average Monthly</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  R {Math.round(avgSalary).toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payslips List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>My Payslips</CardTitle>
              <CardDescription>View and download your payslips</CardDescription>
            </div>
            <Button variant="outline" data-testid="download-all-btn">
              <Download className="w-4 h-4 mr-2" />
              Download All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {payrolls.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payrolls.map((payroll) => (
                <PayslipCard
                  key={payroll.id}
                  payroll={payroll}
                  onClick={setSelectedPayroll}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Payslips Yet</h3>
              <p className="text-slate-500">Your payslips will appear here once processed</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payslip Detail Modal */}
      <PayslipDetail 
        payroll={selectedPayroll} 
        onClose={() => setSelectedPayroll(null)} 
      />
    </div>
  );
};

export default Payslips;
